import { useCallback, useRef, useState } from 'react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import CustomModal, {
  CustomModalBasicProps,
  GridItem,
  GridSection,
} from 'src/components/CustomModal';
import InfoRibbon, { InfoTypography } from 'src/components/InfoRibbon';
import SearchMember from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import {
  CareTeamMember,
  CareTeamMemberWithEditableData,
  CareTeamPermissions,
} from 'src/@types/crs/case';
import { Box, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { updateCareTeamMembers } from '../service/careTeam';
import usePermissionsContext from 'src/careflow/ph-core/admin/permissions/hook/usePermissionsContext';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import NewPractitionerModal from 'src/careflow/care-coordination/practitioner/provider/PractitionerList/NewPractitionerModal/NewPractitionerModal';
import CreatePractitionerRoleDialog from 'src/careflow/care-coordination/practitioner/provider/PractitionerDetails/CreatePractitionerRoleDialog';
import { PractitionerWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';

interface CareTeamMemberModalProps extends CustomModalBasicProps {
  patient?: WrappedPatient;
  careTeamMembers?: CareTeamMember[];
  careTeamId: string;
  onMemberUpdateSuccess: Function;
}

const CareTeamMemberModal = ({
  patient,
  open,
  onClose,
  careTeamMembers,
  careTeamId,
  onMemberUpdateSuccess,
}: CareTeamMemberModalProps) => {

  const { i18n } = useLocales();
  const membersSelected = useRef<CareTeamMemberWithEditableData[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const { componentsData, configurations } = useTenantConfigData();
  const { searchMemberOptions } = componentsData?.searchMember ?? {};
  
  const [isLoading, setIsLoading] = useState(false);
  const [disabledSaved, setDisabledSaved] = useState(false);
  const [isNewPractitionerModalOpen, setIsNewPractitionerModalOpen] = useState(false);
  const [practitioner, setPractitioner] = useState<any>(null);
  const [openPractitionerRolModal, setOpenPractitionerRolModal] = useState(false);
  const [refreshMember, setRefreshMember] = useState(false);
  const [isPractitionerRole, setIsPractitionerRole] = useState(false);

  const { isAllowedToEdit, isAllowedToDelete, isAllowedToAdd } =
    usePermissionsContext<CareTeamPermissions['members']>() ?? {};

  const handleOnSave = useCallback(async () => {
    let error = false;

    if(membersSelected?.current?.length === 0) {
      enqueueSnackbar("Must select at least one member", { variant: 'warning' });
      return;
    }

    const careTeamMembers = membersSelected.current?.map?.((member) => {
      if (member.editableData?.error?.startDate) error = true;
      const memberAssociatedOrgIdEmpty = 
        (member.associatedOrgId === 'N/A' || !member.associatedOrgId);
      return {
        id: member.id,
        resourceType: member.memberType,
        name: member.name,
        identifier: member.identifier,
        identifierType: member.identifierType,
        associatedOrg: member.associatedOrgName,
        associatedOrgId: 
          !memberAssociatedOrgIdEmpty ? 
            member?.associatedOrgId?.includes('Organization') ? 
              member.associatedOrgId : `Organization/${member.associatedOrgId}` : undefined,
        role: member.editableData?.role?.display,
        roleId: member.editableData?.role?.code,
        startDate: member.editableData?.startDate?.toISOString?.(),
        endDate: member.editableData?.endDate?.toISOString?.(),
      };
    });
    if (error) {
      enqueueSnackbar("There's errors in member selection. Please fix them, before proceeding", {
        variant: 'error',
      });
      return;
    }

    const response = await updateCareTeamMembers({ careTeamMembers }, careTeamId);

    if (!response) {
      enqueueSnackbar("There's been an error. Please Try Again", { variant: 'error' });
      return;
    }
    enqueueSnackbar(`${i18n('patients.details.careteams.message', 'crs')} Members succesfully updated`);
    return true;
  }, [careTeamId]);

  const title = [
    i18n('case.details.careteams.titleModal', 'crs'),
    i18n('case.details.careteams.addNewMemberModal', 'crs')
  ];

  const handlerOpenPractitionerRoleModal = (practitioner: any) => {
    if(practitioner){
      const wrappedPractitioner = PractitionerWrapper(practitioner);
      setPractitioner(wrappedPractitioner);
      setIsNewPractitionerModalOpen(false);
      setOpenPractitionerRolModal(true);
    }
  };

  const handlerMemberPractitionerRole = ({isPractitionerRole, disabledSave}: any ) => {
    setDisabledSaved(disabledSave);
    setIsPractitionerRole(isPractitionerRole);
  };

  const handlerClose = () => {
    setDisabledSaved(false);
    setIsPractitionerRole(false);
    onClose?.({}, 'backdropClick');
  };

  return (
    <>
      <CustomModal
        showSaveButton={!!(isAllowedToEdit || isAllowedToDelete || isAllowedToAdd)}
        open={open}
        title={title[1]}
        breadcrumbs={title}
        isSaveButtonDisabled={disabledSaved}
        onSave={async () => {
          setIsLoading(true);
          const wasSaved = await handleOnSave();
          setIsLoading(false);
          if (!wasSaved) return;
          onMemberUpdateSuccess();
          handlerClose();
        }}
        onClose={handlerClose}
        onCancel={handlerClose}
        isLoading={isLoading}
        containerSx={[{ overflow: 'scroll', width: '70vw' }]}
        childrenWithoutPadding={
          <Box mt={3}>
            {!!patient && (
              <InfoRibbon containerSx={{ marginTop: 2 }}>
                <InfoTypography>{`${i18n('case.details.careteams.patient', 'crs')} ${patient.getFullName()}`}</InfoTypography>
                <InfoTypography>
                  Date of Birth {patient.getBirthDateForDisplay()} ({patient.getAgeInYears()} years)
                </InfoTypography>
                <InfoTypography>Identifier {patient.getMRN(configurations?.mpi?.systemMrn)?.value ?? ''}</InfoTypography>
                <InfoTypography>Phone {patient.getPrimaryPhone()?.value ?? ''}</InfoTypography>
                <InfoTypography>Email {patient.getPrimaryEmail()?.value ?? ''}</InfoTypography>
              </InfoRibbon>
            )}
          </Box>
        }
      ><>
        <GridSection mt={2}>
          <GridItem xs={12}>
            <SearchMember
              isLoading={isLoading}
              isEditable={true}
              patient={patient as WrappedPatient}
              careTeamMembers={careTeamMembers}
              membersSelectionRef={membersSelected}
              memberTypesByConfig={searchMemberOptions?.careTeamMemberType}
              handlerMemberPractitionerRole={handlerMemberPractitionerRole}
              externalRefresh={refreshMember}
            />
          </GridItem>
        </GridSection>
        {isPractitionerRole && 
          <Button
            onClick={() => setIsNewPractitionerModalOpen(true)}
            variant="contained"
            disableElevation
            sx={{
              position: 'absolute',
              marginTop: '32px'
            }}
            disabled={isLoading}
          >
           {i18n('practitioner.newPractitionerTitle', 'crs')}
          </Button>
        }
      </>
      </CustomModal>
      {configurations && (
        <NewPractitionerModal
          open={isNewPractitionerModalOpen}
          onClose={() => {
            setIsNewPractitionerModalOpen(false);
          }}
          configurations={configurations}
          handlerActionCreate={handlerOpenPractitionerRoleModal}
        />
      )}
      {!!practitioner && (
        <CreatePractitionerRoleDialog
          onDialogClose={() => {
            setRefreshMember(false);
            setOpenPractitionerRolModal(false);
          }}
          isVisible={openPractitionerRolModal}
          selectedResource={practitioner}
          onPractitionerRoleCreation={() => {
            setRefreshMember(true);
          }}
        />
      )}
    </>
  );
};

export default CareTeamMemberModal;
